import { type DocumentNode, gql } from '@apollo/client';
import { jsonToGraphQLQuery, VariableType } from 'json-to-graphql-query';
import type { FieldValue, GraphQLNodeFields } from './types';

interface SearchResultsFields {
  id: FieldValue;
  trExpressions: FieldValue;
  highlights: FieldValue;
  highlightLabels: FieldValue;
  contentObject: {
    __on: GraphQLNodeFields;
  };
}

interface AssetItemTypeFields {
  __typeName: 'AssetItemType';
  id: FieldValue;
  myAssetPermissions: FieldValue;
}

interface CollectionItemTypeFields {
  __typeName: 'CollectionItemType';
  id: FieldValue;
  collectionType: FieldValue;
  name: FieldValue;
  dateAdded: FieldValue;
  owner: {
    user: {
      username: FieldValue;
    };
    firstName: FieldValue;
    lastName: FieldValue;
    id: FieldValue;
  };
  canShare: FieldValue;
  items?: {
    objectId?: FieldValue;
    id?: FieldValue;
    contentObject?: {
      __on: AssetItemTypeFields;
    };
  };
}

interface ItemSearchQueryResult {
  query: {
    __variables: {
      q: 'String';
      itemType: 'String';
    };
    __name?: string;
    opensearch: {
      __args: {
        q: VariableType;
        itemType: VariableType;
      };
      totalCount: FieldValue;
      start: FieldValue;
      size: FieldValue;
      id: FieldValue;
      results?: SearchResultsFields;
      facets?: FieldValue;
      facetsMeta?: FieldValue;
    };
  };
}

export const searchResults = (truify: boolean = false, node: GraphQLNodeFields = {}): SearchResultsFields => ({
  id: truify,
  trExpressions: truify,
  highlights: truify,
  highlightLabels: truify,
  contentObject: {
    __on: {
      ...node,
    },
  },
});

export const AssetItemType = (truify: boolean = false): AssetItemTypeFields => ({
  __typeName: 'AssetItemType',
  id: truify,
  myAssetPermissions: truify,
});

export const itemSearchQuery = (truify: boolean = false): ItemSearchQueryResult => ({
  query: {
    __variables: {
      q: 'String',
      itemType: 'String',
    },
    opensearch: {
      __args: {
        q: new VariableType('q'),
        itemType: new VariableType('itemType'),
      },
      totalCount: truify,
      start: truify,
      size: truify,
      id: truify,
    },
  },
});

export const buildItemSearchQuery = (node: GraphQLNodeFields = {}, truify: boolean = false): ItemSearchQueryResult => {
  const query = itemSearchQuery(truify);
  const results = searchResults(truify, node);
  query.query.opensearch.results = results;
  return query;
};

export const CollectonItemType = (truify: boolean = false): CollectionItemTypeFields => ({
  __typeName: 'CollectionItemType',
  id: truify,
  collectionType: true,
  name: true,
  dateAdded: truify,
  owner: {
    user: {
      username: truify,
    },
    firstName: truify,
    lastName: truify,
    id: truify,
  },
  canShare: false,
});

export const buildSimpleOSQuery = (): DocumentNode => {
  const asn = AssetItemType(true);
  const q = buildItemSearchQuery(asn, true);
  q.query.__name = 'querytastic';
  const csn = CollectonItemType(true);
  const csnMod: CollectionItemTypeFields = {
    ...csn,
    dateAdded: true,
    items: {
      ...csn.items,
      objectId: true,
      id: true,
      contentObject: {
        __on: {
          ...asn,
        },
      },
    },
  };

  if (q.query.opensearch.results) {
    q.query.opensearch.results.contentObject = {
      ...q.query.opensearch.results.contentObject,
      __on: [{ ...csnMod }, { ...q.query.opensearch.results.contentObject.__on }],
    };
  }

  const gQuery = jsonToGraphQLQuery(q, { pretty: true });
  return gql(gQuery);
};
